import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { selectResources } from '../../../../../slices/userSlice';
import { axiosInstance } from '../../../../../routes/axiosInstance';
import { useBreadcrumbs } from '../../../../../contexts/BreadcrumbContext';
import FormInput from '../../../../../components/FormInput';
import ConfirmDialog from '../../../../../components/ConfirmDialog';

const USE_CASE_OPTIONS = [
  { value: 'wind_power', label: 'Wind Power' },
  { value: 'solar_power', label: 'Solar Power' },
  { value: 'wind_power_ramp', label: 'Wind Power Ramp' },
  { value: 'mfrr_price', label: 'mFRR Price' },
  { value: 'mfrr_energy', label: 'mFRR Energy' },
];

const CHALLENGE_TYPE_OPTIONS = [
  { value: 'day_ahead', label: 'Day-ahead' },
  { value: 'intraday', label: 'Intraday' },
];

const formatUseCaseLabel = (val) =>
  USE_CASE_OPTIONS.find((o) => o.value === val)?.label || val;

const formatChallengeTypeLabel = (val) =>
  CHALLENGE_TYPE_OPTIONS.find((o) => o.value === val)?.label || val;

const ALL_HOURS = Array.from({ length: 24 }, (_, hour) => hour);

const formatGateHour = (hour) => `${String(hour).padStart(2, '0')}:00`;

const normalizeGateClosureHours = (hours) =>
  [...new Set((hours || []).map((hour) => Number(hour)).filter((hour) => Number.isInteger(hour) && hour >= 0 && hour <= 23))]
    .sort((a, b) => a - b);

const formatGateHourSummary = (tmpl) => {
  if (tmpl.all_gate_closures) {
    return 'All Brussels gate closures';
  }

  const hours = normalizeGateClosureHours(tmpl.gate_closure_hours);
  if (hours.length === 0) {
    return 'No gate hours selected';
  }
  if (hours.length === 24) {
    return 'All Brussels gate closures';
  }
  return `Gates: ${hours.map(formatGateHour).join(', ')} Brussels`;
};

const getTemplateHorizonSummary = (tmpl) => {
  if (tmpl.challenge_type === 'intraday') {
    const hours = tmpl.intraday_hours ?? 0;
    return `Window: ${hours} hour${hours !== 1 ? 's' : ''}`;
  }
  return `D+${tmpl.horizon_start_day} (1 day)`;
};

const templateSortKey = (tmpl) => [
  tmpl.use_case || '',
  tmpl.challenge_type || 'day_ahead',
  Number(tmpl.challenge_type === 'intraday' ? tmpl.intraday_hours : tmpl.horizon_start_day) || 0,
];

const compareTemplates = (a, b) => {
  const ka = templateSortKey(a);
  const kb = templateSortKey(b);
  for (let i = 0; i < ka.length; i++) {
    if (ka[i] < kb[i]) return -1;
    if (ka[i] > kb[i]) return 1;
  }
  return 0;
};

const normalizeDayAheadOffset = (value) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) || parsed < 1 ? 1 : parsed;
};

const getDuplicateTemplateMessage = (challengeType) =>
  challengeType === 'intraday'
    ? 'A template already exists for this resource, use case, intraday window, and at least one selected gate hour.'
    : 'A template already exists for this resource, use case, D+ offset, and at least one selected gate hour.';

export default function ChallengeTemplates() {
  useBreadcrumbs(
    [{ label: 'Resources' }, { label: 'Challenge Templates' }],
    'Configure automatic challenge generation per resource'
  );

  const resourcesRaw = useSelector(selectResources);
  const allResources = useMemo(() => resourcesRaw || [], [resourcesRaw]);
  const resources = useMemo(() => allResources.filter((r) => r.is_active !== false), [allResources]);

  const isResourceActive = useCallback(
    (resourceId) => allResources.find((r) => r.id === resourceId)?.is_active !== false,
    [allResources]
  );

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form state
  const emptyForm = {
    resource: '',
    name: '',
    use_case: '',
    challenge_type: 'day_ahead',
    all_gate_closures: false,
    gate_closure_hours: [10],
    horizon_start_day: 1,
    horizon_end_day: 1,
    intraday_hours: '',
    is_active: true,
  };
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null); // template id being edited

  // Filters
  const [filterResource, setFilterResource] = useState('all');

  // Collapsed resource groups in the template list
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());
  const toggleGroup = (resourceId) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(resourceId)) {
        next.delete(resourceId);
      } else {
        next.add(resourceId);
      }
      return next;
    });
  };

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await axiosInstance.get('/market/challenge-template/');
      setTemplates(resp.data?.data || []);
    } catch (err) {
      toast.error('Failed to load challenge templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const resetForm = () => {
    setForm(emptyForm);
    setEditing(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.resource || !form.name || !form.use_case || !form.challenge_type) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (form.challenge_type === 'intraday') {
      const intradayHours = Number(form.intraday_hours);
      if (!Number.isInteger(intradayHours) || intradayHours <= 0) {
        toast.error('Intraday hours must be a positive integer');
        return;
      }
    }
    if (!form.all_gate_closures && normalizeGateClosureHours(form.gate_closure_hours).length === 0) {
      toast.error('Select at least one gate closure hour or choose all gate closures');
      return;
    }
    if (hasDuplicateTemplate) {
      toast.error(getDuplicateTemplateMessage(form.challenge_type));
      return;
    }

    const gateClosureHours = normalizeGateClosureHours(form.gate_closure_hours);
    const payload =
      form.challenge_type === 'intraday'
        ? {
            ...form,
            horizon_start_day: 0,
            horizon_end_day: 0,
            gate_closure_hours: form.all_gate_closures ? [] : gateClosureHours,
            intraday_hours: Number(form.intraday_hours),
          }
        : {
            ...form,
            gate_closure_hours: form.all_gate_closures ? [] : gateClosureHours,
            horizon_start_day: normalizeDayAheadOffset(form.horizon_start_day),
            horizon_end_day: normalizeDayAheadOffset(form.horizon_start_day),
            intraday_hours: null,
          };

    setSaving(true);
    try {
      if (editing) {
        await axiosInstance.patch(`/market/challenge-template/${editing}`, payload);
        toast.success('Template updated');
      } else {
        await axiosInstance.post('/market/challenge-template/', payload);
        toast.success('Template created');
      }
      resetForm();
      fetchTemplates();
    } catch (err) {
      const msg =
        err.response?.data?.data || err.response?.data?.detail || 'Operation failed';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (tmpl) => {
    const allGates = Boolean(tmpl.all_gate_closures);
    const hours = normalizeGateClosureHours(tmpl.gate_closure_hours);
    setEditing(tmpl.id);
    setForm({
      resource: tmpl.resource,
      name: tmpl.name,
      use_case: tmpl.use_case,
      challenge_type: tmpl.challenge_type || 'day_ahead',
      all_gate_closures: allGates,
      gate_closure_hours: allGates ? [] : hours,
      horizon_start_day: tmpl.horizon_start_day,
      horizon_end_day: tmpl.horizon_end_day,
      intraday_hours: tmpl.intraday_hours ?? '',
      is_active: tmpl.is_active && isResourceActive(tmpl.resource),
    });
  };

  const toggleActive = async (tmpl) => {
    try {
      await axiosInstance.patch(`/market/challenge-template/${tmpl.id}`, {
        is_active: !tmpl.is_active,
      });
      toast.success(tmpl.is_active ? 'Template deactivated' : 'Template activated');
      fetchTemplates();
    } catch {
      toast.error('Failed to update template');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axiosInstance.delete(`/market/challenge-template/${deleteTarget.id}`);
      toast.success('Template deleted');
      if (editing === deleteTarget.id) resetForm();
      fetchTemplates();
    } catch {
      toast.error('Failed to delete template');
    } finally {
      setDeleteTarget(null);
    }
  };

  const visibleTemplates = useMemo(() => {
    if (filterResource === 'all') return templates;
    return templates.filter((t) => t.resource === filterResource);
  }, [templates, filterResource]);

  const groupedTemplates = useMemo(() => {
    const byResource = new Map();
    visibleTemplates.forEach((tmpl) => {
      if (!byResource.has(tmpl.resource)) byResource.set(tmpl.resource, []);
      byResource.get(tmpl.resource).push(tmpl);
    });

    return [...byResource.entries()]
      .map(([resourceId, items]) => {
        const resource = allResources.find((r) => r.id === resourceId);
        return {
          resourceId,
          name: resource?.name || items[0]?.resource_name || resourceId,
          timezone: resource?.timezone || '',
          isActive: resource?.is_active !== false,
          templates: [...items].sort(compareTemplates),
          activeCount: items.filter((t) => t.is_active).length,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [visibleTemplates, allResources]);
  const hasDuplicateTemplate = useMemo(
    () => {
      const normalizedOffset = normalizeDayAheadOffset(form.horizon_start_day);
      const intradayHours = Number(form.intraday_hours);
      const formHours = normalizeGateClosureHours(form.gate_closure_hours);

      return templates.some((tmpl) => {
        const templateAll = Boolean(tmpl.all_gate_closures);
        const templateHours = normalizeGateClosureHours(tmpl.gate_closure_hours);
        if (
          tmpl.resource !== form.resource ||
          tmpl.use_case !== form.use_case ||
          (tmpl.challenge_type || 'day_ahead') !== form.challenge_type ||
          tmpl.id === editing
        ) {
          return false;
        }

        const overlapsInHour =
          form.all_gate_closures ||
          templateAll ||
          formHours.some((hour) => templateHours.includes(hour));
        if (!overlapsInHour) {
          return false;
        }

        if (form.challenge_type === 'intraday') {
          return Number(tmpl.intraday_hours) === intradayHours;
        }

        return Number(tmpl.horizon_start_day) === normalizedOffset;
      });
    },
    [
      templates,
      form.resource,
      form.use_case,
      form.challenge_type,
      form.all_gate_closures,
      form.gate_closure_hours,
      form.horizon_start_day,
      form.intraday_hours,
      editing,
    ]
  );

  const isIntraday = form.challenge_type === 'intraday';

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left column: Form */}
        <div className="lg:col-span-1">
          <div className="rounded-lg bg-surface dark:bg-gray-870 shadow-sm border border-gray-200/70 dark:border-gray-600/70 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {editing ? 'Edit Template' : 'New Template'}
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Define which challenges are auto-created when a session opens.
            </p>

            <form onSubmit={handleSubmit} className="mt-4 space-y-4">
              {/* Resource */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Resource
                </label>
                <div className="mt-1 relative">
                  <select
                    value={form.resource}
                    onChange={(e) => {
                      const resourceId = e.target.value;
                      const resourceUseCase = resources.find((r) => r.id === resourceId)?.use_case;
                      setForm({
                        ...form,
                        resource: resourceId,
                        use_case: resourceUseCase || form.use_case,
                      });
                    }}
                    className="block w-full rounded-md border-[0.5px] border-gray-300 dark:border-gray-600 bg-surface dark:bg-gray-870 px-3 py-2 pr-10 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
                    style={{ appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none', backgroundImage: 'none' }}
                  >
                    <option value="">Select resource...</option>
                    {resources.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDownIcon className="h-5 w-5 absolute right-3 top-2.5 text-gray-400 pointer-events-none" />
                </div>
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Template Name
                </label>
                <FormInput
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder={isIntraday ? 'e.g. Wind Intraday 4h' : 'e.g. Wind D+1'}
                />
              </div>

              {/* Use Case */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Use Case
                </label>
                <div className="mt-1 relative">
                  <select
                    value={form.use_case}
                    onChange={(e) => setForm({ ...form, use_case: e.target.value })}
                    className="block w-full rounded-md border-[0.5px] border-gray-300 dark:border-gray-600 bg-surface dark:bg-gray-870 px-3 py-2 pr-10 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
                    style={{ appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none', backgroundImage: 'none' }}
                  >
                    <option value="">Select use case...</option>
                    {USE_CASE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDownIcon className="h-5 w-5 absolute right-3 top-2.5 text-gray-400 pointer-events-none" />
                </div>
                {hasDuplicateTemplate && (
                  <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                    {getDuplicateTemplateMessage(form.challenge_type)}
                  </p>
                )}
              </div>

              {/* Challenge type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Challenge Type
                </label>
                <div className="mt-1 relative">
                  <select
                    value={form.challenge_type}
                    onChange={(e) => {
                      const nextType = e.target.value;
                      setForm({
                        ...form,
                        challenge_type: nextType,
                        horizon_start_day: nextType === 'intraday'
                          ? 0
                          : normalizeDayAheadOffset(form.horizon_start_day),
                        horizon_end_day: nextType === 'intraday'
                          ? 0
                          : normalizeDayAheadOffset(form.horizon_start_day),
                      });
                    }}
                    className="block w-full rounded-md border-[0.5px] border-gray-300 dark:border-gray-600 bg-surface dark:bg-gray-870 px-3 py-2 pr-10 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
                    style={{ appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none', backgroundImage: 'none' }}
                  >
                    {CHALLENGE_TYPE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDownIcon className="h-5 w-5 absolute right-3 top-2.5 text-gray-400 pointer-events-none" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Gate Closures (Brussels)
                </label>
                <label className="mt-2 inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={form.all_gate_closures}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        all_gate_closures: e.target.checked,
                        gate_closure_hours: e.target.checked
                          ? []
                          : normalizeGateClosureHours(form.gate_closure_hours).length > 0
                            ? normalizeGateClosureHours(form.gate_closure_hours)
                            : [10],
                      })
                    }
                    className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                  />
                  Apply to all gate closure hours
                </label>
                {!form.all_gate_closures && (
                  <div className="mt-3 grid grid-cols-6 gap-1.5">
                    {ALL_HOURS.map((hour) => {
                      const isSelected = normalizeGateClosureHours(form.gate_closure_hours).includes(hour);
                      return (
                        <button
                          key={hour}
                          type="button"
                          onClick={() => {
                            const currentHours = normalizeGateClosureHours(form.gate_closure_hours);
                            const nextHours = isSelected
                              ? currentHours.filter((value) => value !== hour)
                              : [...currentHours, hour];
                            setForm({
                              ...form,
                              gate_closure_hours: normalizeGateClosureHours(nextHours),
                            });
                          }}
                          className={`rounded border px-1 py-1.5 text-xs font-medium font-mono tabular-nums ${
                            isSelected
                              ? 'border-brand-500 bg-brand-50 text-brand-700 dark:border-brand-400 dark:bg-brand-500/10 dark:text-brand-300'
                              : 'border-gray-200 text-gray-500 hover:border-gray-300 dark:border-gray-700 dark:text-gray-400'
                          }`}
                        >
                          {String(hour).padStart(2, '0')}
                        </button>
                      );
                    })}
                  </div>
                )}
                {!form.all_gate_closures && normalizeGateClosureHours(form.gate_closure_hours).length > 0 && (
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    Selected: {normalizeGateClosureHours(form.gate_closure_hours).map(formatGateHour).join(', ')}
                  </p>
                )}
              </div>

              {isIntraday ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Intraday Window (hours)
                  </label>
                  <FormInput
                    type="number"
                    min="1"
                    value={form.intraday_hours}
                    onChange={(e) =>
                      setForm({ ...form, intraday_hours: e.target.value })
                    }
                    placeholder="e.g. 4"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Creates one challenge starting at gate closure and lasting for the selected number of hours.
                  </p>
                </div>
              ) : (
                <div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Day-ahead Offset (D+)
                    </label>
                    <FormInput
                      type="number"
                      min="1"
                      value={form.horizon_start_day}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          horizon_start_day: normalizeDayAheadOffset(e.target.value),
                          horizon_end_day: normalizeDayAheadOffset(e.target.value),
                        })
                      }
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Creates one full-day challenge for the selected D+ offset.
                    </p>
                  </div>
                </div>
              )}

              {/* Active toggle */}
              <div className="flex items-center gap-3">
                <label className={`inline-flex items-center ${isResourceActive(form.resource) ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                    disabled={!isResourceActive(form.resource)}
                    className="sr-only"
                  />
                  <span
                    className={`w-11 h-6 flex items-center ${
                      form.is_active ? 'bg-brand-600' : 'bg-gray-200 dark:bg-gray-600'
                    } rounded-full p-1 transition-colors`}
                  >
                    <span
                      className={`bg-white w-4 h-4 rounded-full shadow transform ${
                        form.is_active ? 'translate-x-5' : ''
                      }`}
                    />
                  </span>
                </label>
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Active
                  {!isResourceActive(form.resource) && form.resource && (
                    <span className="ml-1 text-xs text-gray-400">(resource is inactive)</span>
                  )}
                </span>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3 pt-2">
                <button
                  type="submit"
                  disabled={saving || hasDuplicateTemplate}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-brand-500 bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 hover:bg-brand-100 dark:hover:bg-brand-900/50 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {editing ? (
                    <PencilSquareIcon className="h-4 w-4" />
                  ) : (
                    <PlusIcon className="h-4 w-4" />
                  )}
                  {editing ? 'Update' : 'Create'}
                </button>
                {editing && (
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 text-sm text-gray-800 dark:text-gray-100"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>

        {/* Right column: Template list */}
        <div className="lg:col-span-2">
          <div className="rounded-lg bg-surface dark:bg-gray-870 shadow-sm border border-gray-200/70 dark:border-gray-600/70">
            {/* Header with filter */}
            <div className="px-6 py-4 border-b border-gray-200/70 dark:border-gray-600/70 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Challenge Templates
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {visibleTemplates.length} template{visibleTemplates.length !== 1 ? 's' : ''}
                </p>
              </div>
              <div className="relative w-full sm:w-56">
                <select
                  value={filterResource}
                  onChange={(e) => setFilterResource(e.target.value)}
                  className="block w-full rounded-md border-[0.5px] border-gray-300 dark:border-gray-600 bg-surface dark:bg-gray-870 px-3 py-2 pr-10 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
                  style={{ appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none', backgroundImage: 'none' }}
                >
                  <option value="all">All resources</option>
                  {resources.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
                <ChevronDownIcon className="h-5 w-5 absolute right-3 top-2.5 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Template list, grouped by resource */}
            <div>
              {loading ? (
                <div className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">
                  Loading...
                </div>
              ) : visibleTemplates.length === 0 ? (
                <div className="p-6 text-center">
                  <DocumentDuplicateIcon className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600" />
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    No templates yet. Create one to auto-generate challenges.
                  </p>
                </div>
              ) : (
                groupedTemplates.map((group) => {
                  const isCollapsed = collapsedGroups.has(group.resourceId);
                  return (
                    <div
                      key={group.resourceId}
                      className="border-b border-gray-200/70 dark:border-gray-600/70 last:border-b-0"
                    >
                      {/* Resource group header */}
                      <button
                        type="button"
                        onClick={() => toggleGroup(group.resourceId)}
                        className="w-full px-4 sm:px-6 py-3 flex items-center gap-2 bg-gray-50 dark:bg-gray-800/60 hover:bg-gray-100 dark:hover:bg-gray-700/50 text-left"
                      >
                        {isCollapsed ? (
                          <ChevronRightIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        ) : (
                          <ChevronDownIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        )}
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {group.name}
                        </span>
                        {group.timezone && (
                          <span className="hidden sm:inline text-xs text-gray-400 dark:text-gray-500">
                            {group.timezone}
                          </span>
                        )}
                        {!group.isActive && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium border border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400">
                            Resource inactive
                          </span>
                        )}
                        <span className="ml-auto text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                          {group.activeCount} active / {group.templates.length}
                        </span>
                      </button>

                      {/* Compact template rows */}
                      {!isCollapsed && (
                        <div className="divide-y divide-gray-100 dark:divide-gray-700">
                          {group.templates.map((tmpl) => {
                            const effectiveActive =
                              tmpl.is_active && group.isActive;
                            return (
                              <div
                                key={tmpl.id}
                                className={`px-4 sm:px-6 py-2.5 flex flex-col gap-1 sm:grid sm:grid-cols-[minmax(0,1.5fr)_minmax(0,0.9fr)_minmax(0,0.8fr)_minmax(0,1.6fr)_auto] sm:items-center sm:gap-3 ${
                                  editing === tmpl.id
                                    ? 'bg-brand-50 dark:bg-brand-900/20'
                                    : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'
                                }`}
                              >
                                <div className="flex items-center gap-2 min-w-0">
                                  <span
                                    title={effectiveActive ? 'Active' : 'Inactive'}
                                    className={`h-2 w-2 rounded-full flex-shrink-0 ${
                                      effectiveActive
                                        ? 'bg-green-500'
                                        : 'bg-gray-300 dark:bg-gray-600'
                                    }`}
                                  />
                                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                    {tmpl.name}
                                  </span>
                                </div>
                                <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                  {formatUseCaseLabel(tmpl.use_case)}
                                </span>
                                <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                  {formatChallengeTypeLabel(tmpl.challenge_type || 'day_ahead')}
                                </span>
                                <span className="text-xs text-gray-400 dark:text-gray-500 truncate">
                                  {formatGateHourSummary(tmpl)} • {getTemplateHorizonSummary(tmpl)}
                                </span>
                                <div className="flex items-center gap-1 flex-shrink-0 sm:justify-end">
                                  <button
                                    onClick={() => toggleActive(tmpl)}
                                    title={
                                      !group.isActive
                                        ? 'Resource is inactive'
                                        : tmpl.is_active ? 'Deactivate' : 'Activate'
                                    }
                                    disabled={!group.isActive}
                                    className={`p-1.5 rounded-md ${
                                      !group.isActive
                                        ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                                        : tmpl.is_active
                                        ? 'text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20'
                                        : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                                    }`}
                                  >
                                    {tmpl.is_active ? (
                                      <CheckCircleIcon className="h-4 w-4" />
                                    ) : (
                                      <XCircleIcon className="h-4 w-4" />
                                    )}
                                  </button>
                                  <button
                                    onClick={() => startEdit(tmpl)}
                                    title="Edit"
                                    className="p-1.5 rounded-md dark:text-gray-400 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                                  >
                                    <PencilSquareIcon className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => setDeleteTarget(tmpl)}
                                    title="Delete"
                                    className="p-1.5 rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={deleteTarget !== null}
        title="Delete Template"
        description={`Are you sure you want to delete "${deleteTarget?.name || 'this template'}"? Existing challenges generated from this template will not be affected.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
        iconBg="bg-red-100"
        iconColor="text-red-600"
      />
    </div>
  );
}
